import { Controller } from "@hotwired/stimulus"

/**
 * Submits the item form from the top-right header submit button (Update Item on
 * edit, Create Item on new) — CONEK-5178. The button lives in the header slot, a
 * separate DOM subtree from the <form> in the page body, so we resolve the form
 * by id (there is no bottom action bar; the header is the only submit).
 *
 * The button also carries native type="submit" form="item-form" as a graceful
 * fallback: if this controller never attaches (JS error, blocked assets), the
 * browser submits the form on its own. When the controller IS attached we
 * preventDefault() on the click so the native submit and requestSubmit() don't
 * both fire (double submit); requestSubmit() runs the form's normal validation +
 * Turbo/Rails submit path.
 *
 * CONEK-6538: an empty required attribute (~76% of the catalogue) used to
 * swallow the click with no message, no marked field and no request — the
 * browser's bubble anchored to a hidden Tom Select <select>. We now surface the
 * block ourselves before delegating to requestSubmit(). Only an empty required
 * field is blocked here (CONEK-7911); any other invalid value goes on to
 * requestSubmit(). The form is meant to be `novalidate` but is not (form_with
 * drops a bare `novalidate: true`), so the browser still validates there.
 *
 * CONEK-6220: saving a changed Status only updates Item#status — the per-branch
 * statuses (ItemOnSpaceBranch) are left alone, which users read as "the status
 * did not apply". Since this is the page's only submit path, the prompt hooks in
 * here: when the Status select moved we hold the submit, open the confirmation
 * dialog, and let the user's choice write the hidden opt-in field before the form
 * goes out. The modal lives in the page body, a different DOM subtree from this
 * controller (which sits on the header button), so its buttons are wired
 * imperatively rather than through a Stimulus action.
 */
export default class extends Controller {
  static values = {
    formId: String,
    // Message shown when a required field is empty. "%{field}" is replaced with
    // the offending field's label. Passed in from the view so it stays
    // translatable; the fallback only applies if the value is missing.
    invalidMessage: { type: String, default: "%{field} is required" },
    // Separate plural template: the number of blocking fields is only known in
    // the browser, so I18n pluralisation cannot pick the right form server-side.
    invalidMessagePlural: { type: String, default: "%{field} are required" },
    invalidMessageFallback: { type: String, default: "This field is required" },
    // The admin layout renders <html translate="no"> with no `lang`, unlike
    // application.html.erb — so document.documentElement.lang is always "" on
    // this page and cannot drive Intl. The view passes I18n.locale instead.
    locale: { type: String, default: "en" },
    // CONEK-6220. Empty id = the prompt is off (flag off, high-risk item, or the
    // "new item" form); the submit then goes straight through.
    statusModalId: String,
    // The status the server rendered, to tell a real change from a resubmit.
    originalStatus: String,
    statusFieldName: { type: String, default: "item[status]" },
    propagateFieldId: {
      type: String,
      default: "item-propagate-status-to-branches",
    },
  }

  connect() {
    // Scopes the modal button listeners to this controller's lifetime, so a
    // Turbo navigation cannot leave a stale handler submitting a dead form.
    this.branchPromptAbort = new AbortController()
    this.branchPromptWired = false
  }

  disconnect() {
    this.branchPromptAbort?.abort()
  }

  submit(event) {
    const form = document.getElementById(this.formIdValue)
    if (!form) {
      console.warn(
        `catalog--item-form: no form with id "${this.formIdValue}"; falling back to native submit`,
      )
      return
    }
    event.preventDefault()

    this.clearInvalid()

    // Only an empty required field is blocked here, because that is the one
    // thing this controller's message ("… is required") describes. Blocking any
    // other native invalidity (a step mismatch such as 10.123 in a 0.01-step
    // field) reported it as "is required" (CONEK-7911 QA rework). Those now go
    // to requestSubmit(), where the browser's own validation explains them in
    // its own words. Note the item form is NOT actually `novalidate`:
    // form_with ignores a bare `novalidate: true` option (it needs `html:`).
    const blocking = this.blockingFields(form)
    if (blocking.length > 0) {
      this.revealInvalid(form, blocking)
      return
    }

    // CONEK-6220: a changed Status needs an explicit answer on whether to also
    // push it onto the per-branch statuses. The validation block above wins —
    // there is nothing to confirm about a form that cannot be submitted.
    if (this.shouldPromptForBranchPropagation(form)) {
      this.openBranchPropagationPrompt(form)
      return
    }

    form.requestSubmit()
  }

  // ── CONEK-6220: branch status propagation prompt ──────────────────────────

  get branchPromptDialog() {
    return this.statusModalIdValue
      ? document.getElementById(this.statusModalIdValue)
      : null
  }

  shouldPromptForBranchPropagation(form) {
    if (!this.branchPromptDialog) return false
    // Read the NATIVE <select>: Tom Select hides it but keeps its value in sync,
    // so this is the same value the form will actually post.
    const field = form.elements.namedItem(this.statusFieldNameValue)
    if (!field) return false
    return field.value !== this.originalStatusValue
  }

  openBranchPropagationPrompt(form) {
    const dialog = this.branchPromptDialog
    // Defensive: never strand the user on a click that does nothing.
    if (!dialog) {
      form.requestSubmit()
      return
    }

    if (!this.branchPromptWired) {
      dialog
        .querySelectorAll("[data-status-propagation]")
        .forEach((button) =>
          button.addEventListener(
            "click",
            () =>
              this.applyBranchPropagationChoice(
                form,
                button.dataset.statusPropagation === "branches",
              ),
            { signal: this.branchPromptAbort?.signal },
          ),
        )
      this.branchPromptWired = true
    }

    // Go through the ui--modal controller so the focus trap, scroll lock and
    // animation all run; showModal() is only the fallback for the frame before
    // that controller has attached.
    const modal = this.modalControllerFor(dialog)
    if (modal) modal.open()
    else dialog.showModal()
  }

  applyBranchPropagationChoice(form, propagate) {
    const field = document.getElementById(this.propagateFieldIdValue)
    if (field) field.value = propagate ? "1" : "0"

    const dialog = this.branchPromptDialog
    if (dialog) {
      const modal = this.modalControllerFor(dialog)
      if (modal) modal.close()
      else dialog.close()
    }

    form.requestSubmit()
  }

  modalControllerFor(dialog) {
    return this.application.getControllerForElementAndIdentifier(
      dialog,
      "ui--modal",
    )
  }

  // Mark every invalid field, then scroll to and focus the first one. All of
  // them are marked so a legacy item missing several required attributes does
  // not turn into whack-a-mole, one click per field.
  blockingFields(form) {
    return [...form.querySelectorAll(":invalid")].filter(
      (field) => field.validity?.valueMissing,
    )
  }

  revealInvalid(form, fields) {
    if (fields.length === 0) return

    fields.forEach((field) => this.mark(field))
    // Remember what we marked so clearInvalid only ever undoes our own work —
    // server-rendered aria-invalid (business-rule errors like uniqueness, which
    // native checkValidity() cannot see and this controller could never
    // re-flag) must survive a retry click untouched.
    this.markedFields = fields

    this.announce(form, this.messageFor(fields))

    // Scroll to a field the banner actually names: messageFor() drops fields
    // whose label does not resolve, so raw DOM order could focus an unnamed
    // field while the message talks about a later, named one. Only when nothing
    // resolved a label — the message is generic then — does order decide.
    const first = fields.find((f) => this.labelFor(f)) || fields[0]
    first.scrollIntoView({ behavior: "smooth", block: "center" })
    // A ui--select hides the native control, so focus the visible input the
    // wrapper exposes; use the field itself when there is no wrapper.
    const focusable =
      this.selectWrapperFor(first)?.querySelector(
        "input:not([type='hidden']), [tabindex]",
      ) || first
    // Focus after the scroll starts so the browser does not fight it.
    requestAnimationFrame(() => focusable.focus({ preventScroll: true }))
  }

  mark(field) {
    field.setAttribute("aria-invalid", "true")
    // ui--select wraps the native <select> in its own control; the `error`
    // class is the styling hook that controller already looks for on connect.
    this.selectWrapperFor(field)?.classList.add("error")
  }

  clearInvalid() {
    this.markedFields?.forEach((field) => {
      field.removeAttribute("aria-invalid")
      this.selectWrapperFor(field)?.classList.remove("error")
    })
    this.markedFields = []
    this.errorElement?.remove()
    this.errorElement = null
  }

  selectWrapperFor(field) {
    return field.closest("[data-controller~='ui--select']")
  }

  messageFor(fields) {
    const labels = fields.map((f) => this.labelFor(f)).filter(Boolean)
    // Without a single resolvable label there is nothing specific to name.
    if (labels.length === 0) return this.invalidMessageFallbackValue
    // Agreement follows how many fields actually block the submit, not how many
    // labels resolved — otherwise two blockers with one nameable label would
    // read as singular and under-report the second.
    const template =
      fields.length > 1
        ? this.invalidMessagePluralValue
        : this.invalidMessageValue
    return template.replace(
      "%{field}",
      // Intl.ListFormat gives a locale-correct "A, B and C" without shipping a
      // separate conjunction translation for every locale. The locale comes
      // from the view (I18n.locale), not <html lang>, which the admin layout
      // never sets.
      new Intl.ListFormat(this.localeValue, {
        style: "long",
        type: "conjunction",
      }).format(labels),
    )
  }

  // Ui::InputComponent emits <label for="{field id}">, but Ui::SelectComponent
  // points its label at TomSelect's visible control ("{select id}-ts-control")
  // rather than the native <select> we validate — so a `for` match on the
  // field's own id misses every ui--select field. Verified against the rendered
  // page: the Material attribute's label carries
  // for="select-item[attribute_values][…]-ts-control". Both ids are tried; we
  // deliberately do NOT walk out to a parent looking for any nearby label —
  // sibling attribute fields share a parent, so that mislabels one field with
  // another's name ("Material and Material").
  labelFor(field) {
    const label =
      this.labelByFor(field.id) ||
      this.labelByFor(field.id && `${field.id}-ts-control`)
    if (!label) return null
    // Labels carry a trailing "*" required marker; drop it from the message.
    return label.textContent.trim().replace(/\s*\*$/, "") || null
  }

  labelByFor(id) {
    // Attribute field ids embed the definition UUID in brackets, so they must
    // be escaped before going into a selector.
    return id ? document.querySelector(`label[for="${CSS.escape(id)}"]`) : null
  }

  announce(form, message) {
    const banner = document.createElement("div")
    // role=alert so screen readers announce it without needing focus.
    banner.setAttribute("role", "alert")
    banner.dataset.itemFormError = "true"
    banner.className =
      "flex items-start gap-3 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-xs font-semibold text-red-800 dark:border-red-700 dark:bg-red-950/40 dark:text-red-200"
    banner.textContent = message

    form.insertAdjacentElement("afterbegin", banner)
    this.errorElement = banner
  }
}
